'use client';

import React from 'react';

interface SectionErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  retryCount: number;
}

interface SectionErrorBoundaryProps {
  children: React.ReactNode;
  componentName: string;
  sectionTitle?: string;
  minHeight?: string;
}

export class SectionErrorBoundary extends React.Component<
  SectionErrorBoundaryProps,
  SectionErrorBoundaryState
> {
  constructor(props: SectionErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, retryCount: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<SectionErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    try {
      if (typeof window !== 'undefined') {
        import('@/lib/error-logger').then(({ ErrorLogger }) => {
          ErrorLogger.log(error, {
            componentName: this.props.componentName,
            severity: 'high',
          });
        }).catch(() => {});
      }
    } catch {
      // silently ignore logging errors
    }
    console.error(`[SectionErrorBoundary] ${this.props.componentName}:`, error, errorInfo);
  }

  handleRetry = () => {
    try {
      if (typeof window !== 'undefined') {
        import('@/lib/error-logger').then(({ ErrorLogger }) => {
          ErrorLogger.trackUserAction(`retry_${this.props.componentName}`);
        }).catch(() => {});
      }
    } catch {
      // silently ignore
    }
    this.setState((prev) => ({
      hasError: false,
      error: undefined,
      retryCount: prev.retryCount + 1,
    }));
  };

  render() {
    const { hasError, error, retryCount } = this.state;
    const { sectionTitle, componentName, minHeight = 'min-h-[200px]' } = this.props;

    if (hasError) {
      return (
        <div
          className={`${minHeight} flex items-center justify-center bg-surface/50 border border-border rounded-xl mx-4 my-6`}
        >
          <div className="text-center max-w-sm mx-auto px-6 py-8">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-6 h-6 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground mb-2">
              {sectionTitle || componentName} Unavailable
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              This section encountered an error and couldn&apos;t load.
              {retryCount > 0 && (
                <span className="block mt-1 text-xs text-muted-foreground/70">
                  Retry attempt {retryCount}
                </span>
              )}
            </p>
            {error && (
              <p className="text-xs text-red-400 bg-red-50 rounded p-2 mb-4 text-left font-mono break-all">
                {error.message}
              </p>
            )}
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-secondary transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default SectionErrorBoundary;
